/**
 * Register — Fase B migration to useZodForm (iter-208).
 *
 * Same pattern as Login.jsx. Honeypot + lang-picker pills allowlisted
 * with explicit design-system comments. Shared-op preview and referral
 * code flow are untouched.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { useAuth } from "../../contexts/AuthContext";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { formatApiError, api } from "../../lib/api";
import { useSecurityConfig } from "../../lib/useSecurityConfig";
import { LumfiWordmark } from "../../components/LumfiLogo";
import { TurnstileGate } from "../../components/TurnstileGate";
import { Users } from "lucide-react";
import { z } from "zod";
import { useZodForm, FormField, SubmitButton } from "../../components/form-ux";

export default function Register() {
  const { register } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: securityConfig } = useSecurityConfig();
  const captchaEnabled = !!securityConfig?.turnstile?.enabled;
  const isEs = lang === "es";

  const sharedOpToken = searchParams.get("shared_op") || (() => {
    try { return sessionStorage.getItem("lumfi_pending_shared_op") || ""; } catch { return ""; }
  })();
  const [sharedPreview, setSharedPreview] = useState(null);

  const referralCode = searchParams.get("ref") || (() => {
    try { return sessionStorage.getItem("lumfi_pending_ref") || ""; } catch { return ""; }
  })();
  useEffect(() => {
    if (!referralCode) return;
    try { sessionStorage.setItem("lumfi_pending_ref", referralCode); } catch { /* private mode */ }
  }, [referralCode]);

  // Non-form state
  const [website, setWebsite] = useState("");
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [waitingForCaptcha, setWaitingForCaptcha] = useState(false);
  const [topError, setTopError] = useState("");
  const pendingSubmitRef = useRef(null);

  const RegisterSchema = z.object({
    email: z.string().trim().min(1, tx(lang, "Email is required.", "El email es obligatorio.", "O e-mail é obrigatório."))
      .email(tx(lang, "Invalid email.", "Email inválido.", "E-mail inválido.")),
    password: z.string().min(6, tx(lang, "Min 6 characters.", "Mínimo 6 caracteres.", "Mínimo 6 caracteres.")),
  });

  const form = useZodForm(RegisterSchema, { email: "", password: "" }, { formId: "register" });

  // Pre-fill from shared-op invite payload when ready.
  useEffect(() => {
    if (!sharedOpToken) return;
    try { sessionStorage.setItem("lumfi_pending_shared_op", sharedOpToken); } catch { /* private mode */ }
    api.get(`/shared/by-token/${sharedOpToken}`)
      .then((r) => {
        setSharedPreview(r.data);
        const sp = (r.data.splits || []).find((x) => x.claim_token === sharedOpToken);
        if (sp?.email && !form.values.email) {
          form.setValue("email", sp.email);
        }
      })
      .catch(() => { /* token expired/invalid */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedOpToken]);

  const doRegister = async (creds) => {
    setTopError("");
    setWaitingForCaptcha(false);
    pendingSubmitRef.current = null;
    try {
      const data = await register(creds.email, creds.password, {
        website: creds.website,
        turnstile_token: creds.turnstileToken || undefined,
        ref: referralCode || undefined,
      });
      if (data?.pending_verification) {
        try { sessionStorage.removeItem("lumfi_pending_ref"); } catch { /* private */ }
        navigate(`/verify-email-pending?email=${encodeURIComponent(creds.email)}`);
        return;
      }
      if (sharedOpToken) {
        try {
          await api.post(`/shared/by-token/${sharedOpToken}/claim`);
          try { sessionStorage.removeItem("lumfi_pending_shared_op"); } catch { /* private */ }
          navigate("/shared");
          return;
        } catch {
          // Best-effort — fall through to /onboarding.
        }
      }
      navigate("/onboarding");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "captcha_expired") {
        setTurnstileToken("");
        setWaitingForCaptcha(true);
        pendingSubmitRef.current = { ...creds, turnstileToken: "" };
        setTopError(formatApiError(detail));
        return;
      }
      setTopError(formatApiError(detail) || e.message);
    }
  };

  const onSubmit = form.handleSubmit(async (values) => {
    if (captchaEnabled && !turnstileToken) {
      setTopError(tx(lang,
        "Security check in progress… try again in a second.",
        "Verificación de seguridad en curso… reintenta en un segundo.",
        "Verificação de segurança em andamento… tente novamente.",
      ));
      pendingSubmitRef.current = { email: values.email, password: values.password, website, turnstileToken: "" };
      setWaitingForCaptcha(true);
      return;
    }
    await doRegister({ email: values.email, password: values.password, website, turnstileToken });
  });

  const handleToken = (tok) => {
    setTurnstileToken(tok);
    if (tok) setTopError("");
    if (tok && pendingSubmitRef.current) {
      const queued = pendingSubmitRef.current;
      pendingSubmitRef.current = null;
      doRegister({ ...queued, turnstileToken: tok });
    }
  };

  useEffect(() => () => {
    pendingSubmitRef.current = null;
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10 safe-bottom">
      <div className="w-full max-w-md lumfi-rise-in">
        <div className="text-center mb-7 relative">
          <Link to="/" className="inline-flex items-center justify-center" aria-label="Lumfi">
            <LumfiWordmark height={40} />
          </Link>
          <div
            className="absolute top-0 right-0 inline-flex bg-white border border-slate-200 rounded-full p-0.5 shadow-sm"
            data-testid="register-lang-picker"
          >
            {["es", "en", "pt"].map((code) => (
              // design-system: ALLOW lang-picker toggle pill (3-state segmented control, no Button variant matches)
              <button /* design-system: ALLOW legacy-todo */
                key={code}
                type="button"
                onClick={() => setLang(code)}
                data-testid={`register-lang-${code}`}
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${
                  lang === code
                    ? "bg-emerald-600 text-white"
                    : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
        </div>

        <Card className="p-6 md:p-8 rounded-2xl border-slate-200 shadow-sm">
          {sharedPreview && (
            <div
              className="mb-5 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 flex items-start gap-2"
              data-testid="register-shared-preview"
            >
              <Users className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <strong>
                  {isEs
                    ? "Te invitaron a una operación compartida"
                    : "You were invited to a shared operation"}
                </strong>
                <div className="mt-0.5 text-emerald-800/85">
                  {sharedPreview.concept} · {sharedPreview.total_amount} {sharedPreview.currency}
                  <br />
                  {isEs
                    ? "Al registrarte, vamos a vincular esta operación a tu cuenta."
                    : "Sign-up will link this operation to your account automatically."}
                </div>
              </div>
            </div>
          )}
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
            {isEs ? "Crea tu cuenta" : t("register_title")}
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">
            {isEs
              ? "Lleva el control de tu caja en menos de 2 minutos."
              : "Get your cash under control in under 2 minutes."}
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="register-form" noValidate>
            <FormField
              label={t("email")}
              name="email"
              required
              error={form.errors.email && (form.touched.email || form.submitCount > 0) ? form.errors.email : undefined}
            >
              <FormField.Input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="tú@ejemplo.com"
                data-testid="register-email-input"
                className="h-12 text-base rounded-xl"
                {...form.getFieldProps("email")}
              />
            </FormField>

            <FormField
              label={t("password")}
              name="password"
              required
              hint={isEs ? "Mínimo 6 caracteres" : "Min 6 characters"}
              error={form.errors.password && (form.touched.password || form.submitCount > 0) ? form.errors.password : undefined}
            >
              <FormField.Input
                type="password"
                autoComplete="new-password"
                data-testid="register-password-input"
                className="h-12 text-base rounded-xl"
                {...form.getFieldProps("password")}
              />
            </FormField>

            {topError && (
              <div
                data-testid="register-error"
                className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                role="alert"
              >
                {topError}
              </div>
            )}

            {/* design-system: ALLOW honeypot anti-bot input (must be hidden, raw) */}
            <input /* design-system: ALLOW legacy-todo */
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              data-testid="register-honeypot"
              className="absolute opacity-0 -left-[9999px] h-0 w-0"
            />

            <TurnstileGate action="register" lang={lang} onToken={handleToken} />

            <SubmitButton
              submitting={form.submitting || waitingForCaptcha}
              loadingText={
                waitingForCaptcha
                  ? (isEs ? "Reintentando…" : "Retrying…")
                  : t("loading")
              }
              testid="register-submit-btn"
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-base font-semibold"
            >
              {isEs ? "Crear cuenta" : t("sign_up")}
            </SubmitButton>

            <p className="text-[11px] text-slate-400 text-center leading-relaxed">
              {isEs
                ? <>Al crear tu cuenta aceptas los <Link to="/legal/terms" className="text-slate-600 hover:text-slate-900 underline">Términos</Link> y la <Link to="/legal/privacy" className="text-slate-600 hover:text-slate-900 underline">Privacidad</Link>.</>
                : <>By creating an account you accept the <Link to="/legal/terms" className="text-slate-600 hover:text-slate-900 underline">Terms</Link> and <Link to="/legal/privacy" className="text-slate-600 hover:text-slate-900 underline">Privacy Policy</Link>.</>
              }
            </p>
          </form>

          <div className="mt-6 text-sm text-slate-600 text-center">
            {isEs ? "¿Ya tienes cuenta?" : t("have_account")}{" "}
            <Link to="/login" className="text-emerald-700 font-semibold hover:underline">
              {isEs ? "Inicia sesión" : t("sign_in")}
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
